<script setup lang="ts">
// Card BLOQUEADO ("Em breve") do hub — composto em DOM, não uma arte exportada,
// exatamente o caminho do RouletteVipCard.vue (os VIP 0→6): o componente recebe
// as frases por prop e monta gradiente + cadeado + texto + arte no CSS. Ele NÃO
// conhece store nem contrato, então sobe sozinho num harness.
//
// Por que DOM e não uma segunda arte por card: o Figma só tem a variante
// bloqueada no MOBILE (`21455:68828`, o `Property 1=Variant2` do component set
// do check-in) — o desenho desktop existe apenas num node ESCONDIDO
// (`21461:69901`, draft do layout antigo de linha inteira). Com imagem seriam
// dois arquivos por card, um deles sem desenho de origem, e a copy ficaria
// assada. Aqui a copy vem do contrato (`lockedLabel` + `title`) e o texto é DOM
// de verdade — o leitor de tela lê "Em breve. Check-in Diário".
//
// Substituiu o tratamento anterior (arte ativa com `blur(1px)` + ribbon de
// cadeado no canto): a variante do Figma não é a arte ativa desfocada, é outro
// card — fundo preto/stone, bloco centralizado e a arte em ESCALA DE CINZA.
const props = defineProps<{
  // Rótulo grande — o `lockedLabel` do contrato ("Em breve", "Volta amanhã"...).
  label: string;
  // Linha de baixo: o título do card no contrato ("Check-in Diário").
  title: string;
  // Arte cinza (opcional): só o check-in tem hoje. Sem ela o card fica no
  // gradiente + cadeado + texto, que já é um estado completo.
  art?: string;
  layout?: 'full' | 'half';
}>();

const layout = computed(() => props.layout ?? 'full');
</script>

<template>
  <div
    class="lc"
    :class="`lc--${layout}`"
  >
    <!-- A arte sangra ACIMA do corpo (é assim no Figma) e precisa ser cortada
         pelo canto arredondado de baixo. Quem corta é esta caixa, não o card: o
         card não pode ter `overflow: hidden`, senão come a própria sangria.
         Mesma mecânica do `.rvc__art`. -->
    <span
      v-if="art"
      class="lc__art"
    >
      <img
        :alt="''"
        aria-hidden="true"
        class="lc__img"
        decoding="async"
        loading="lazy"
        :src="art"
      />
    </span>

    <span class="lc__content">
      <NuxtIcon
        class="lc__lock"
        filled
        name="loyalty-hub/lock-keyhole"
      />

      <span class="lc__text">
        <span class="lc__label">{{ label }}</span>
        <span class="lc__title">{{ title }}</span>
      </span>
    </span>
  </div>
</template>

<style scoped lang="scss">
// Medidas em `cqw` (1cqw = 1% da largura do card: 3,74px no desenho mobile,
// 5,515px no desktop), cada valor sendo `medida_do_figma / largura`. O motivo de
// não usar `%` está comentado no RouletteVipCard: dentro da coluna a
// porcentagem passa a valer sobre a caixa dela, não sobre o card.
// Cada declaração `cqw` vem depois de uma em `px` (o valor do desenho), que é o
// fallback pra navegador sem container query.

/* ================= MOBILE (base) — card 374 × 88 ================= */
.lc {
  container-type: inline-size;
  position: relative;
  width: 100%;
  border-radius: 12px;
  // Gradiente da variante bloqueada: preto → stone/800. Nada de tier aqui, então
  // as cores são fixas (diferente do RouletteVipCard, que recebe as duas pontas).
  background-image: linear-gradient(131.3deg, #000 18.212%, #292524 99.919%);
  // A arte passa do topo — o card não pode cortar.
  overflow: visible;
  // Card bloqueado não é alvo de clique: nem cursor, nem hit-test. Sem isto a
  // caixa dele engoliria o toque em vez de deixar passar (o `.lh-card--inert` do
  // Card.vue resolve isso pra arte estática).
  pointer-events: none;
  text-align: left;
  // Sem borda: o Figma tem um traço de `rgba(255,255,255,.05)` em volta, mesma
  // decisão tomada no card de VIP — na tela ele só suja o contorno contra o
  // painel.
  border: 0;
  // ATENÇÃO: nada de `cqw` NESTE seletor — um elemento não consulta a si mesmo
  // (resolveria contra a viewport). E `padding` aqui encolheria a régua de todos
  // os descendentes, porque `cqw` deles é 1% da CONTENT-BOX do container.
  padding: 0;
}

.lc--full {
  aspect-ratio: 374 / 88;
}

// Caixa de clip da arte = o retângulo do desenho, medido no node: 109,907 de
// largura (29,3869%) encostado na direita, terminando na base do card e subindo
// pela PROPORÇÃO — a 374 de card isso dá 101,83 de altura, ou seja o topo em
// −13,83, que é a sangria do Figma.
// A altura sai de `aspect-ratio`, e não de um `top` em %, porque % de altura se
// mede na altura do CARD: quando o card muda de proporção (a linha de 3 do
// desktop é 362 × 164, não 551,5 × 164) a arte esticava na vertical. Ancorada na
// base com proporção fixa ela escala igual às artes dos cards ativos.
// O clip existe pelo canto: o card não pode ter `overflow: hidden` (comeria a
// própria sangria), então quem arredonda o canto inferior-direito é esta caixa —
// e o raio fica no navegador, não assado no `.webp`, pra acompanhar o card em
// qualquer tamanho. A caixa NÃO pode ser mais estreita que a arte: com os 99 da
// moldura do Figma (em vez dos 109,907 da imagem) sobrava um corte reto de 8px na
// borda esquerda da arte — medido no harness contra o export do node.
.lc__art {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 29.3869%;
  aspect-ratio: 109.907 / 101.83;
  overflow: hidden;
  border-bottom-right-radius: 12px;
}

// O arquivo é o quadrado de origem inteiro (1024², sem recorte) redimensionado
// pro tamanho POSICIONADO no desenho, então ele preenche a caixa de clip 1:1
// (109,907 × 101,9 contra 109,907 × 101,83 de caixa — 0,07 de diferença).
// O espelhamento (`-scale-y-100 rotate-180` do Figma) está assado no arquivo,
// pra não gastar `transform`.
.lc__img {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  height: 100%;
  user-select: none;
  -webkit-user-drag: none;
}

// Bloco cadeado + texto, centralizado. O `padding-right` de 18px (4,8128%) é o
// que reproduz o enquadramento do Figma: lá o bloco é centrado numa caixa de 356
// ancorada à esquerda, ou seja o centro dele cai 9px à esquerda do centro do
// card, e não em cima da arte.
.lc__content {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  gap: 2.139cqw;
  padding-right: 18px;
  padding-right: 4.8128%;
}

// Caixa do ícone 32,934² com o vetor de 21,697 × 24,797 dentro (o desenho reserva
// 17,06% de folga de cada lado) — caixa e miolo têm medida própria, um não é o
// outro esticado.
.lc__lock {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 32.934px;
  width: 8.8064cqw;
  height: 32.934px;
  height: 8.8064cqw;

  :deep(svg) {
    display: block;
    width: 21.697px;
    width: 5.8014cqw;
    height: 24.797px;
    height: 6.6303cqw;
    // NuxtIcon injeta o <svg> com margin-bottom próprio.
    margin-bottom: 0;
  }
}

.lc__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  gap: 0.2674cqw;
}

// "Em breve": 14/600 numa linha de 20.
.lc__label {
  color: #fff;
  font-size: 14px;
  font-size: 3.7433cqw;
  font-weight: 600;
  line-height: 1.4286;
  white-space: nowrap;
}

// Título do card: 10/400.
.lc__title {
  color: #fff;
  font-size: 10px;
  font-size: 2.6738cqw;
  font-weight: 400;
  line-height: 1.26;
  white-space: nowrap;
}

/* ---------- `half` no mobile (179 × 164) — EXTRAPOLAÇÃO ---------- */
// O Figma não tem card `half` bloqueado. A referência usada foi o `half` ATIVO,
// que inverte a composição do `full`: arte grande no TOPO e texto embaixo. Então
// o bloqueado empilha igual — arte centrada em cima, cadeado acima das frases,
// tudo centrado na base — em vez de manter a arte na direita, que num card de 179
// de largura ficaria uma tira esmagada.
// Só abaixo de 1024: no desktop todo card vira 2 colunas (551,5 × 164) e a
// composição volta a ser a de linha, com as regras da base.
.lc--half {
  aspect-ratio: 179 / 164;
}

@media (max-width: 1023.98px) {
  // A arte não sangra no `half` (o ativo também não — ver o `aspect-ratio` sem
  // `margin-top` no Card.vue), então aqui ela é um bloco normal, com a proporção
  // vinda do arquivo, e sem caixa de clip: nada encosta nos cantos do card.
  // 50% de largura (89,5 → 83 de altura pela proporção do arquivo) começando a 3%
  // do topo: é o que deixa a arte terminar ~8px ACIMA do cadeado. Com os 62% da
  // primeira tentativa o cadeado subia por cima do calendário.
  .lc--half .lc__art {
    top: 3%;
    right: auto;
    bottom: auto;
    left: 50%;
    width: 50%;
    height: auto;
    // Aqui a altura vem da imagem (ela é bloco normal dentro da caixa), não de
    // uma proporção declarada.
    aspect-ratio: auto;
    overflow: visible;
    border-bottom-right-radius: 0;
    transform: translateX(-50%);
  }

  .lc--half .lc__img {
    position: static;
    width: 100%;
    height: auto;
  }

  // Cadeado ACIMA das frases (a coluna é estreita pra deixar os dois na mesma
  // linha) e o bloco ancorado na base.
  .lc--half .lc__content {
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 5px;
    gap: 2.7933cqw;
    padding-right: 0;
    padding-bottom: 8px;
    padding-bottom: 4.4693cqw;
  }

  .lc--half .lc__text {
    align-items: center;
  }

  // Os tamanhos do texto NÃO acompanham a largura do card aqui: 1cqw no `half`
  // é 1,79px contra 3,74 no `full`, então herdar os `cqw` da base deixaria as
  // frases pela metade. Os valores são os mesmos PX do desenho mobile, pra dois
  // cards bloqueados na mesma tela lerem igual.
  // Cadeado em 28 (e não nos 32,934 do `full`): num card de 179 de largura os
  // 32,934 pesam demais ao lado das frases empilhadas. A folga da caixa em volta
  // do vetor segue a mesma proporção do desenho.
  .lc--half .lc__lock {
    width: 28px;
    width: 15.6425cqw;
    height: 28px;
    height: 15.6425cqw;

    :deep(svg) {
      width: 18.45px;
      width: 10.3073cqw;
      height: 21.08px;
      height: 11.7765cqw;
    }
  }

  .lc--half .lc__label {
    font-size: 14px;
    font-size: 7.8212cqw;
  }

  .lc--half .lc__title {
    font-size: 10px;
    font-size: 5.5866cqw;
  }
}

/* ================= DESKTOP — card 551,5 × 164 ================= */
// Não existe variante bloqueada desktop publicada: o único desenho é o node
// ESCONDIDO `21461:69901`, do layout antigo em que o check-in ocupava a linha
// inteira (1139,35 × 164). O que foi aproveitado dele — porque é geometria, não
// chute — é o enquadramento: bloco centralizado e arte de 205,47 × 190,5 ancorada
// à direita (sangrando 3,73 pra fora e 26,88 acima) e à base. Na largura de hoje
// (551,5, 2 colunas) essa arte dá 37,3% do card, contra 35,7% da arte do card
// ATIVO desktop — ou seja o draft e o layout atual concordam no tamanho.
// A tipografia é o que NÃO veio do draft: lá ela ficou nos 14/10 do mobile dentro
// de um card de 164. Aqui ela sobe pros tamanhos dos cards desktop vizinhos
// (22,45/16,04), pro card bloqueado não ficar miúdo ao lado dos ativos.
// O cadeado não precisa de override: os 32,934 em `cqw` já viram 48,56 aqui.
@media (min-width: 1024px) {
  .lc--full,
  .lc--half {
    aspect-ratio: 551.5 / 164;
  }

  .lc__art {
    // (205,47 − 3,73) / 551,5 — a sangria lateral fica fora do card, então é
    // cortada aqui em vez de pairar sobre o gap do grid.
    width: 36.58%;
    // 201,74 × 190,88: ancorada na base, sobe 26,88 acima do corpo (a sangria do
    // desenho). A proporção difere da do mobile porque a caixa corta 3,73 da
    // largura da imagem.
    aspect-ratio: 201.74 / 190.88;
  }

  // Última linha do grid desktop (3 colunas) — o span vem do Card.vue, a razão do
  // corpo é esta. A classe é a mesma que o Card.vue põe na raiz do filho.
  // A arte NÃO acompanha a largura do card aqui: nos cards ativos dessa linha o
  // Figma redesenhou a arte menor em vez de encolher tudo (o `descontos-loja`, por
  // exemplo, tem 159,6 × 169 num card de 362 × 164, sangrando só ~5). Os 46% põem
  // a arte bloqueada na mesma faixa: 166,5 × 157,5, terminando na base e sem
  // sangria — cabe inteira no corpo do card estreito.
  // E o bloco de texto passa a ser centrado no que SOBRA à esquerda da arte
  // (`padding-right` = a largura dela): centrado no card inteiro, como nos outros
  // dois formatos, ele corria por baixo do calendário.
  .lc.lh-card--last3 {
    aspect-ratio: 362 / 164;

    .lc__art {
      width: 46%;
    }

    .lc__content {
      padding-right: 46%;
    }
  }

  .lc__img {
    left: 0;
    // 205,47 / 201,74 (a caixa de clip)
    width: 101.85%;
  }

  .lc__label {
    font-size: 22.45px;
    font-size: 4.0709cqw;
    // 28,865 / 22,451 — a ALTURA DA CAIXA do Figma, não o `leading` que ele
    // reporta (mesma armadilha comentada no RouletteVipCard).
    line-height: 1.2857;
  }

  .lc__title {
    font-size: 16.04px;
    font-size: 2.9083cqw;
    line-height: 1.38;
  }
}
</style>
